<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useI18n } from 'vue-i18n';
import {
  buildConversationChannelInbox,
  resolveConversationChannelType,
  getConversationSenderIdentifier,
} from 'dashboard/helper/conversationHelper';
import { getConversationChannelColorClass } from 'dashboard/helper/conversationChannelTheme';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();
const { updateUISettings } = useUISettings();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta || {});

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() => {
  const senderId = props.chat?.meta?.sender?.id;
  return senderId ? store.getters['contacts/getContact'](senderId) : {};
});

const displayName = computed(() => {
  return (
    currentContact.value?.name ||
    props.chat?.meta?.sender?.name ||
    props.chat?.meta?.sender?.identifier ||
    'Unknown contact'
  );
});

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});
const inboxSettings = computed(() => inbox.value?.settings || {});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const resolvedChannelType = computed(() =>
  resolveConversationChannelType({
    chat: props.chat,
    contact: currentContact.value,
    inbox: inbox.value,
  })
);

const channelIconInbox = computed(() =>
  buildConversationChannelInbox({
    chat: props.chat,
    contact: currentContact.value,
    inbox: inbox.value,
  })
);

const channelIconClass = computed(() =>
  getConversationChannelColorClass(resolvedChannelType.value)
);

const senderIdentifier = computed(() =>
  getConversationSenderIdentifier({
    chat: props.chat,
    contact: currentContact.value,
  })
);

const autoStatusEnabled = ref(false);
const autoStatusLoading = ref(false);
const autoStatusSaving = ref(false);
const autoStatusResolved = ref(false);
const DEFAULT_AUTO_STATUS_ENDPOINT =
  'https://app.egcircle.com/webhook/getautostat';
const DEFAULT_AUTO_SWITCH_ENDPOINT = 'https://app.egcircle.com/webhook/switch';
const TRUE_STATUS_VALUES = ['true', 'on', '1'];
const FALSE_STATUS_VALUES = ['false', 'off', '0', 'fales'];
const autoStatusEndpoint = computed(
  () =>
    inboxSettings.value.auto_switch_status_url || DEFAULT_AUTO_STATUS_ENDPOINT
);
const autoSwitchEndpoint = computed(
  () =>
    inboxSettings.value.auto_switch_trigger_url || DEFAULT_AUTO_SWITCH_ENDPOINT
);

const normalizeAutoStatusValue = value => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  if (typeof value !== 'string') {
    return false;
  }

  const normalizedValue = value.trim().toLowerCase();

  if (TRUE_STATUS_VALUES.includes(normalizedValue)) {
    return true;
  }

  if (FALSE_STATUS_VALUES.includes(normalizedValue)) {
    return false;
  }

  return false;
};

const extractAutoStatusValue = payload => {
  if (payload === null || payload === undefined) {
    return false;
  }

  if (Array.isArray(payload)) {
    return extractAutoStatusValue(payload[0]);
  }

  if (typeof payload !== 'object') {
    return normalizeAutoStatusValue(payload);
  }

  const candidateKeys = [
    'status',
    'n8n_stats',
    'enabled',
    'active',
    'value',
  ];

  for (const key of candidateKeys) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      return normalizeAutoStatusValue(payload[key]);
    }
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'data')) {
    return extractAutoStatusValue(payload.data);
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'result')) {
    return extractAutoStatusValue(payload.result);
  }

  return false;
};

const loadAutoStatus = async () => {
  if (!senderIdentifier.value) {
    autoStatusEnabled.value = false;
    autoStatusResolved.value = true;
    return;
  }

  autoStatusResolved.value = false;
  autoStatusLoading.value = true;

  try {
    const response = await window.fetch(
      `${autoStatusEndpoint.value}?senderId=${encodeURIComponent(
        senderIdentifier.value
      )}`
    );

    if (!response.ok) {
      throw new Error(`Status request failed: ${response.status}`);
    }

    const responseText = (await response.text()).trim();

    try {
      const responseJson = JSON.parse(responseText);
      autoStatusEnabled.value = extractAutoStatusValue(responseJson);
    } catch (parseError) {
      autoStatusEnabled.value = normalizeAutoStatusValue(responseText);
    }
  } catch (error) {
    autoStatusEnabled.value = false;
  } finally {
    autoStatusResolved.value = true;
    autoStatusLoading.value = false;
  }
};

const updateAutoStatus = async nextValue => {
  if (!senderIdentifier.value || autoStatusSaving.value) return;

  const previousValue = autoStatusEnabled.value;
  autoStatusEnabled.value = nextValue;
  autoStatusSaving.value = true;

  try {
    const response = await window.fetch(autoSwitchEndpoint.value, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        senderId: senderIdentifier.value,
        sender_id: senderIdentifier.value,
        n8n_stats: nextValue,
        status: nextValue,
        enabled: nextValue,
      }),
    });

    if (!response.ok) {
      throw new Error(`Toggle request failed: ${response.status}`);
    }

    await loadAutoStatus();
  } catch (error) {
    autoStatusEnabled.value = previousValue;
    useAlert('Unable to update auto status');
  } finally {
    autoStatusSaving.value = false;
  }
};

const openConversationProfilePanel = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
  });
};

watch(senderIdentifier, () => {
  loadAutoStatus();
}, { immediate: true });
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 py-2 border-b bg-n-background border-n-weak h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="displayName"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-2 p-0 m-0">
          <div
            v-if="channelIconInbox"
            class="flex items-center justify-center size-5 rounded-full bg-n-slate-2 dark:bg-n-slate-3 flex-shrink-0"
          >
            <ChannelIcon
              :inbox="channelIconInbox"
              class="size-4"
              :class="channelIconClass"
            />
          </div>
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ displayName }}
          </span>
          <div
            v-if="senderIdentifier && !autoStatusResolved"
            class="ml-1 h-4 w-7 rounded-full bg-n-slate-4 animate-pulse flex-shrink-0"
          />
          <ToggleSwitch
            v-else-if="senderIdentifier"
            v-model="autoStatusEnabled"
            class="ml-1"
            :disabled="autoStatusLoading || autoStatusSaving"
            @change="updateAutoStatus"
          />
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
        >
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <NextButton
        v-tooltip.left="'Conversation File'"
        slate
        sm
        faded
        icon="i-ph-user-circle-bold"
        @click="openConversationProfilePanel"
      />
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
